import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getStaffDashboard, type GetStaffDashboardOutputType } from 'zite-endpoints-sdk';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RefreshCw, AlertCircle, Users, Search, Database, Clock,
  UserX, Shield, XCircle,
} from 'lucide-react';

type StaffMember = NonNullable<GetStaffDashboardOutputType['canonicalRoster']>[0];

function SourceBanner({ fetchedAt, staffCount, loading, onRetry }: {
  fetchedAt: string | undefined;
  staffCount: number;
  loading: boolean;
  onRetry: () => void;
}) {
  if (!fetchedAt) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-2">
      <Database className="w-3.5 h-3.5" />
      <span>Source: <strong className="text-foreground">BMS readiness</strong></span>
      <span className="hidden sm:inline">·</span>
      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(fetchedAt).toLocaleTimeString()}</span>
      <span>· {staffCount} staff</span>
      <Button variant="ghost" size="sm" className="h-6 px-2 ml-auto" onClick={onRetry} disabled={loading}>
        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );
}

function StaffCard({ member }: { member: StaffMember }) {
  const initials = member.displayName
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const roleCls: Record<string, string> = {
    owner: 'bg-primary/15 text-primary border-primary/30',
    duty_manager: 'bg-purple-500/15 text-purple-500 border-purple-500/30',
    manager: 'bg-purple-500/15 text-purple-500 border-purple-500/30',
  };
  const cls = roleCls[member.role] || 'bg-muted text-muted-foreground border-border';

  return (
    <div className="flex items-center gap-3 border border-border rounded-lg px-4 py-3 hover:bg-muted/30 transition-colors">
      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{member.displayName}</p>
        <p className="text-xs text-muted-foreground truncate">
          {member.department || '—'}
          <span className="mx-1">·</span>
          {member.trainingStatus === 'assessed'
            ? <span className="text-emerald-500">{member.trainingPercent}% trained</span>
            : <span>Not assessed</span>}
        </p>
      </div>
      <Badge className={`text-[10px] shrink-0 ${cls}`}>
        {member.role.replace(/_/g, ' ')}
      </Badge>
      {member.active
        ? <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Active" />
        : <span className="w-2 h-2 rounded-full bg-muted-foreground shrink-0" title="Inactive" />}
    </div>
  );
}

export default function TeamRoster() {
  const { user, isLoading: authLoading, loginWithRedirect } = useAuth();
  const [data, setData] = useState<GetStaffDashboardOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!authLoading && !user) loginWithRedirect();
  }, [authLoading, user, loginWithRedirect]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getStaffDashboard({});
      setData(result);
      if (!result.canonicalRoster || result.canonicalRoster.length === 0) {
        setError('Readiness data unavailable — the upstream BMS roster could not be fetched.');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to fetch staff directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (authLoading || !user) return null;

  const staff = data?.canonicalRoster || [];
  const filtered = search.trim()
    ? staff.filter(s =>
        s.displayName.toLowerCase().includes(search.toLowerCase()) ||
        s.role.toLowerCase().includes(search.toLowerCase()) ||
        s.department.toLowerCase().includes(search.toLowerCase())
      )
    : staff;

  const owners = filtered.filter(s => s.role === 'owner');
  const managers = filtered.filter(s => s.role === 'duty_manager' || s.role === 'manager');
  const others = filtered.filter(s => !['owner', 'duty_manager', 'manager'].includes(s.role));

  if (loading && !data) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  if (error && !staff.length) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <AlertCircle className="w-12 h-12 text-destructive" />
          <h2 className="text-lg font-semibold">Staff Directory Unavailable</h2>
          <p className="text-sm text-muted-foreground max-w-md">{error}</p>
          <Button onClick={load}><RefreshCw className="w-4 h-4 mr-2" /> Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Team Roster
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {staff.length} canonical staff from BMS readiness
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="space-y-3 mb-4">
        <SourceBanner
          fetchedAt={data?.canonicalRosterFetchedAt}
          staffCount={staff.length}
          loading={loading}
          onRetry={load}
        />
        <div className="flex items-center gap-2 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <UserX className="w-3.5 h-3.5 text-amber-500" />
          <span>Viewing as <strong>{user.email}</strong> — roster is read-only canonical data, no local duplicates.</span>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, role or department…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Shifts / checklists honest status */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="border border-border rounded-lg p-3 flex items-center gap-2">
          <XCircle className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-xs font-medium">Shifts</p>
            <p className="text-[11px] text-muted-foreground">No PlanDay context</p>
          </div>
        </div>
        <div className="border border-border rounded-lg p-3 flex items-center gap-2">
          <XCircle className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-xs font-medium">Checklists</p>
            <p className="text-[11px] text-muted-foreground">Not connected</p>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No staff match "{search}"</p>
      ) : (
        <div className="space-y-4">
          {owners.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> Owners ({owners.length})
              </h3>
              <div className="space-y-1.5">{owners.map(s => <StaffCard key={s.sourceStaffId} member={s} />)}</div>
            </div>
          )}
          {managers.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Managers ({managers.length})
              </h3>
              <div className="space-y-1.5">{managers.map(s => <StaffCard key={s.sourceStaffId} member={s} />)}</div>
            </div>
          )}
          {others.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Staff ({others.length})
              </h3>
              <div className="space-y-1.5">{others.map(s => <StaffCard key={s.sourceStaffId} member={s} />)}</div>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground text-center mt-6">
        Source: BMS readiness · phone.rbcomms.co.uk/api/integrations/staff-hub/readiness · No local duplicates · No PII exposed
      </p>
    </div>
  );
}
